package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.HomeSectionApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminSectionDto
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get
import org.w3c.xhr.FormData
import org.w3c.xhr.XMLHttpRequest
import kotlin.js.Promise

/**
 * Banner Editor Component (T085, T087, T088, T089)
 *
 * Features:
 * - T085: Dual file upload inputs (English/Arabic)
 * - T087: File size indicator showing upload progress and final size
 * - T088: Image preview after upload to verify appearance
 * - T089: Optional link URL input with URL format validation
 * - UPDATED: Defers image upload until section save
 */
@Composable
fun BannerEditor(
    section: AdminSectionDto?,
    homeSectionApiClient: HomeSectionApiClient,
    onUpdate: () -> Unit = {},
    onDataChanged: (String, String?, String?, String?) -> Unit = { _, _, _, _ -> },
    onFilesChanged: (File?, File?) -> Unit = { _, _ -> }
) {
    val colors = AppTheme.colors

    // State for banner fields
    var internalName by remember { mutableStateOf(section?.internalName ?: "") }
    var bannerLink by remember { mutableStateOf(section?.bannerLink ?: "") }
    var bannerLinkError by remember { mutableStateOf<String?>(null) }

    // English banner state
    var bannerImageEnUrl by remember { mutableStateOf(section?.bannerImageEn ?: "") }
    var bannerImageEnFile by remember { mutableStateOf<File?>(null) }
    var bannerImageEnPreview by remember { mutableStateOf<String?>(null) }
    var bannerImageEnSize by remember { mutableStateOf<Long?>(null) }
    var uploadingEn by remember { mutableStateOf(false) }
    var uploadProgressEn by remember { mutableStateOf(0) }

    // Arabic banner state
    var bannerImageArUrl by remember { mutableStateOf(section?.bannerImageAr ?: "") }
    var bannerImageArFile by remember { mutableStateOf<File?>(null) }
    var bannerImageArPreview by remember { mutableStateOf<String?>(null) }
    var bannerImageArSize by remember { mutableStateOf<Long?>(null) }
    var uploadingAr by remember { mutableStateOf(false) }
    var uploadProgressAr by remember { mutableStateOf(0) }

    // General state
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Notify parent when form data changes
    LaunchedEffect(internalName, bannerImageEnUrl, bannerImageArUrl, bannerLink) {
        onDataChanged(
            internalName,
            bannerImageEnUrl.takeIf { it.isNotBlank() },
            bannerImageArUrl.takeIf { it.isNotBlank() },
            bannerLink.takeIf { it.isNotBlank() }
        )
    }

    // Notify parent when files are selected (for deferred upload)
    LaunchedEffect(bannerImageEnFile, bannerImageArFile) {
        onFilesChanged(bannerImageEnFile, bannerImageArFile)
    }

    // T089: URL validation function
    fun validateUrl(url: String): String? {
        if (url.isBlank()) return null // Optional field

        val urlPattern = Regex(
            "^(https?://)" +
            "([a-zA-Z0-9.-]+\\.[a-zA-Z]{2,})" +
            "(:\\d+)?" +
            "(/.*)?$"
        )

        return when {
            !urlPattern.matches(url) -> "Invalid URL format"
            !url.startsWith("https://") -> "HTTPS is preferred for security"
            else -> null
        }
    }

    // T087: Format file size for display
    fun formatFileSize(bytes: Long): String {
        return when {
            bytes < 1024 -> "$bytes B"
            bytes < 1024 * 1024 -> "${bytes / 1024} KB"
            else -> {
                val mb = bytes.toDouble() / (1024 * 1024)
                val rounded = (mb * 100).toInt() / 100.0
                "$rounded MB"
            }
        }
    }

    // T088: Handle file selection and preview generation
    fun handleFileSelect(file: File, language: String) {
        val maxSize = 10 * 1024 * 1024 // 10MB
        val fileSize = file.size.toLong()

        if (fileSize > maxSize) {
            error = "File size exceeds 10MB limit"
            return
        }

        if (!file.type.startsWith("image/")) {
            error = "Only image files are allowed"
            return
        }

        // Generate preview
        val reader = FileReader()
        reader.onload = {
            val dataUrl = reader.result as String
            if (language == "eng") {
                bannerImageEnFile = file
                bannerImageEnPreview = dataUrl
                bannerImageEnSize = fileSize
            } else {
                bannerImageArFile = file
                bannerImageArPreview = dataUrl
                bannerImageArSize = fileSize
            }
            null
        }
        reader.readAsDataURL(file)
    }

    // T087: Upload banner image with progress
    fun uploadBannerImage(file: File, language: String) {
        section?.id?.let { sectionId ->
            scope.launch {
                try {
                    if (language == "eng") {
                        uploadingEn = true
                        uploadProgressEn = 0
                    } else {
                        uploadingAr = true
                        uploadProgressAr = 0
                    }

                    // Create FormData for multipart upload
                    val formData = FormData()
                    formData.append("file", file)
                    formData.append("language", language)

                    // Get auth token
                    val token = window.localStorage.getItem("id_token")

                    // Use XMLHttpRequest for progress tracking
                    val xhr = XMLHttpRequest()

                    xhr.upload.addEventListener("progress", { event ->
                        val progressEvent = event.asDynamic()
                        if (progressEvent.lengthComputable as Boolean) {
                            val percentComplete = ((progressEvent.loaded as Double) / (progressEvent.total as Double) * 100).toInt()
                            if (language == "eng") {
                                uploadProgressEn = percentComplete
                            } else {
                                uploadProgressAr = percentComplete
                            }
                        }
                    })

                    xhr.addEventListener("load", {
                        if (xhr.status == 200.toShort()) {
                            val response = JSON.parse<dynamic>(xhr.responseText)
                            if (language == "eng") {
                                bannerImageEnUrl = response.key as String
                                success = "English banner uploaded successfully"
                            } else {
                                bannerImageArUrl = response.key as String
                                success = "Arabic banner uploaded successfully"
                            }
                            onUpdate()
                        } else {
                            error = "Upload failed: ${xhr.statusText}"
                        }

                        if (language == "eng") {
                            uploadingEn = false
                            uploadProgressEn = 0
                        } else {
                            uploadingAr = false
                            uploadProgressAr = 0
                        }
                    })

                    xhr.addEventListener("error", {
                        error = "Upload failed: Network error"
                        if (language == "eng") {
                            uploadingEn = false
                            uploadProgressEn = 0
                        } else {
                            uploadingAr = false
                            uploadProgressAr = 0
                        }
                    })

                    xhr.open("POST", "/api/admin/sections/$sectionId/upload-banner")
                    if (!token.isNullOrBlank()) {
                        xhr.setRequestHeader("Authorization", "Bearer $token")
                    }
                    xhr.send(formData)

                } catch (e: Exception) {
                    error = "Upload failed: ${e.message}"
                    if (language == "eng") {
                        uploadingEn = false
                        uploadProgressEn = 0
                    } else {
                        uploadingAr = false
                        uploadProgressAr = 0
                    }
                }
            }
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            themeTransition()
        }
    }) {
        // Error/Success messages
        error?.let { msg ->
            Div({
                style {
                    padding(Spacing.sm, Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.md)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    fontSize(Typography.base)
                    themeTransition()
                }
            }) {
                Text(msg)
                Button({
                    onClick { error = null }
                    style {
                        marginLeft(Spacing.sm)
                        padding(2.px, 6.px)
                        backgroundColor(Color.transparent)
                        border(0.px)
                        cursor("pointer")
                        color(Color(colors.text))
                    }
                }) { Text("✕") }
            }
        }

        success?.let { msg ->
            Div({
                style {
                    padding(Spacing.sm, Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#d1fae5" else "#052e16"))
                    color(Color(if (colors == LightColors) "#065f46" else "#86efac"))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.md)
                    fontSize(Typography.base)
                    themeTransition()
                }
            }) { Text(msg) }
        }

        // Internal name
        Div({ style { marginBottom(Spacing.md) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontSize(13.px); fontWeight("600"); color(Color(colors.text)) } }) {
                Text("Internal Name")
            }
            Input(InputType.Text) {
                attr("type", "text")
                attr("value", internalName)
                attr("placeholder", "e.g., Hero Banner")
                onInput { internalName = it.value }
                style {
                    width(100.percent)
                    padding(6.px, Spacing.sm)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    fontSize(Typography.base)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    property("box-sizing", "border-box")
                }
            }
        }

        // Banner link URL
        Div({ style { marginBottom(Spacing.lg) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontSize(13.px); fontWeight("600"); color(Color(colors.text)) } }) {
                Text("Link URL (Optional)")
            }
            Input(InputType.Text) {
                attr("type", "url")
                attr("value", bannerLink)
                attr("placeholder", "https://example.com/promo")
                onInput {
                    bannerLink = it.value
                    bannerLinkError = validateUrl(it.value)
                }
                style {
                    width(100.percent)
                    padding(6.px, Spacing.sm)
                    border(1.px, LineStyle.Solid, if (bannerLinkError != null) Color(colors.danger) else Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    fontSize(Typography.base)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    property("box-sizing", "border-box")
                }
            }
            bannerLinkError?.let { err ->
                Div({ style { fontSize(Typography.sm); color(if (err.contains("preferred")) Color(colors.warning) else Color(colors.danger)); marginTop(3.px) } }) {
                    Text(err)
                }
            }
        }

        // English and Arabic banners side-by-side
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "${Spacing.md}")
            }
        }) {
            // English banner column
            Div({ style { flex("1") } }) {
                Div({ style { fontSize(13.px); fontWeight("600"); marginBottom(6.px); color(Color(colors.text)) } }) {
                    Text("English Banner")
                }
                Label(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        padding(Spacing.md, Spacing.sm)
                        border(1.px, LineStyle.Dashed, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        textAlign("center")
                        cursor("pointer")
                        property("min-height", "60px")
                        backgroundColor(Color(colors.surface))
                    }
                }) {
                    Input(InputType.File) {
                        ref { input ->
                            input.onchange = {
                                val file = (input as HTMLInputElement).files?.get(0)
                                file?.let { handleFileSelect(it, "eng") }
                                null
                            }
                            onDispose { }
                        }
                        style { display(DisplayStyle.None) }
                        attr("accept", "image/*")
                    }

                    // Show small preview or upload prompt
                    val enPreview = bannerImageEnPreview
                    val enUrl = bannerImageEnUrl
                    if (enPreview != null) {
                        Img(src = enPreview) {
                            style {
                                width(80.px)
                                height(48.px)
                                property("object-fit", "cover")
                                borderRadius(3.px)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                marginBottom(Spacing.xs)
                            }
                        }
                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                            Text(bannerImageEnFile?.name?.take(20) ?: "")
                        }
                    } else if (enUrl.isNotBlank()) {
                        Img(src = enUrl) {
                            style {
                                width(80.px)
                                height(48.px)
                                property("object-fit", "cover")
                                borderRadius(3.px)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                marginBottom(Spacing.xs)
                            }
                        }
                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                            Text("Click to change")
                        }
                    } else {
                        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                            Text("+ Upload")
                        }
                    }
                }
            }

            // Arabic banner column
            Div({ style { flex("1") } }) {
                Div({ style { fontSize(13.px); fontWeight("600"); marginBottom(6.px); color(Color(colors.text)) } }) {
                    Text("Arabic Banner (العربية)")
                }
                Label(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        padding(Spacing.md, Spacing.sm)
                        border(1.px, LineStyle.Dashed, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        textAlign("center")
                        cursor("pointer")
                        property("min-height", "60px")
                        backgroundColor(Color(colors.surface))
                    }
                }) {
                    Input(InputType.File) {
                        ref { input ->
                            input.onchange = {
                                val file = (input as HTMLInputElement).files?.get(0)
                                file?.let { handleFileSelect(it, "ara") }
                                null
                            }
                            onDispose { }
                        }
                        style { display(DisplayStyle.None) }
                        attr("accept", "image/*")
                    }

                    // Show small preview or upload prompt
                    val arPreview = bannerImageArPreview
                    val arUrl = bannerImageArUrl
                    if (arPreview != null) {
                        Img(src = arPreview) {
                            style {
                                width(80.px)
                                height(48.px)
                                property("object-fit", "cover")
                                borderRadius(3.px)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                marginBottom(Spacing.xs)
                            }
                        }
                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                            Text(bannerImageArFile?.name?.take(20) ?: "")
                        }
                    } else if (arUrl.isNotBlank()) {
                        Img(src = arUrl) {
                            style {
                                width(80.px)
                                height(48.px)
                                property("object-fit", "cover")
                                borderRadius(3.px)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                marginBottom(Spacing.xs)
                            }
                        }
                        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                            Text("Click to change")
                        }
                    } else {
                        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                            Text("+ Upload")
                        }
                    }
                }
            }
        }
    }
}
